import { useCallback, useEffect } from 'react';

import { useActions } from '../../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../../shared/common/hooks/useTypedSelector';

/**
 * Возвращаемые значения хука.
 */
interface UseDashboardFullscreenReturn {
  /** Флаг полноэкранного режима */
  isFullscreen: boolean;
  /** Включить полноэкранный режим */
  enterFullscreen: () => void;
  /** Выйти из полноэкранного режима */
  exitFullscreen: () => void;
  /** Переключить полноэкранный режим */
  toggleFullscreen: () => void;
}

/**
 * Хук для управления полноэкранным режимом дашборда.
 * Использует нативный Fullscreen API браузера + Redux state (meta.isFullscreen).
 * MainLayout скрывает header/sidebar при isFullscreen.
 */
export const useDashboardFullscreen = (): UseDashboardFullscreenReturn => {
  const isFullscreen = useTypedSelector((state) => state.meta.isFullscreen);
  const { setMetaFullscreen } = useActions();

  const enterFullscreen = useCallback(() => {
    document.documentElement.requestFullscreen().then(() => {
      setMetaFullscreen(true);
    }).catch(() => {
      // Fallback: если нативный fullscreen заблокирован, включаем только Redux-режим
      setMetaFullscreen(true);
    });
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().then(() => {
        setMetaFullscreen(false);
      }).catch(() => {
        setMetaFullscreen(false);
      });
    } else {
      setMetaFullscreen(false);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setMetaFullscreen(true);
      }).catch(() => {
        setMetaFullscreen(true);
      });
    } else {
      document.exitFullscreen().then(() => {
        setMetaFullscreen(false);
      }).catch(() => {
        setMetaFullscreen(false);
      });
    }
  }, []);

  // Синхронизация Redux при выходе из fullscreen через ESC (браузер сам обрабатывает ESC)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setMetaFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Горячие клавиши: F11 или Shift+Ctrl+F для полноэкранного режима
  useEffect(() => {
    const handleFullscreenKey = (e: KeyboardEvent) => {
      const isShiftCtrlF = e.shiftKey && (e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F');
      if (e.key === 'F11' || isShiftCtrlF) {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    document.addEventListener('keydown', handleFullscreenKey);
    return () => document.removeEventListener('keydown', handleFullscreenKey);
  }, [toggleFullscreen]);

  // Сбрасываем fullscreen при размонтировании
  useEffect(() => {
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      setMetaFullscreen(false);
    };
  }, []);

  return {
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
  };
};

export default useDashboardFullscreen;
